/**
 * Arbeitsfreie Tage für die Kalender der Datumsauswahl (OpenProject #206).
 *
 * Kundenwunsch: Samstage, Sonntage und Feiertage im Kalender farbig
 * hervorheben. Maßgeblich sind die gesetzlichen Feiertage in
 * **Nordrhein-Westfalen** (Feiertagsgesetz NW §2) — beide Breuer-Standorte
 * (Goch, Korschenbroich) liegen dort. Bewusst nicht enthalten: Heiligabend,
 * Silvester, Brückentage und Feiertage anderer Bundesländer am Lieferort.
 * Ostersonntag und Pfingstsonntag sind in NRW keine gesetzlichen Feiertage,
 * als Sonntage aber ohnehin markiert.
 *
 * React-frei und ohne dayjs, damit `node --test` es direkt ausführen kann
 * (siehe `german_holidays.test.ts`). Monate sind hier 1-basiert (1 = Januar).
 */

export type NonWorkingDay =
  | { kind: 'holiday'; name: string }
  | { kind: 'weekend' };

/** Ostersonntag nach der Gauß'schen Osterformel (Meeus/Jones/Butcher). */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = h + l - 7 * m + 114;
  return { month: Math.floor(n / 31), day: (n % 31) + 1 };
}

function key(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// UTC, damit Sommerzeitwechsel beim Addieren von Tagen nichts verschieben.
function keyFromUtc(date: Date): string {
  return key(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

const cache = new Map<number, ReadonlyMap<string, string>>();

/** Gesetzliche Feiertage NRW eines Jahres, Schlüssel `YYYY-MM-DD`. */
export function nrwHolidays(year: number): ReadonlyMap<string, string> {
  const cached = cache.get(year);
  if (cached) return cached;

  const easter = easterSunday(year);
  const relativeToEaster = (offsetDays: number) =>
    keyFromUtc(new Date(Date.UTC(year, easter.month - 1, easter.day + offsetDays)));

  const holidays = new Map<string, string>([
    [key(year, 1, 1), 'Neujahr'],
    [relativeToEaster(-2), 'Karfreitag'],
    [relativeToEaster(1), 'Ostermontag'],
    [key(year, 5, 1), 'Tag der Arbeit'],
    [relativeToEaster(39), 'Christi Himmelfahrt'],
    [relativeToEaster(50), 'Pfingstmontag'],
    [relativeToEaster(60), 'Fronleichnam'],
    [key(year, 10, 3), 'Tag der Deutschen Einheit'],
    [key(year, 11, 1), 'Allerheiligen'],
    [key(year, 12, 25), '1. Weihnachtstag'],
    [key(year, 12, 26), '2. Weihnachtstag'],
  ]);
  cache.set(year, holidays);
  return holidays;
}

/**
 * Ist der Tag arbeitsfrei? Ein Feiertag geht dem Wochenende vor (Allerheiligen
 * 2026 ist ein Sonntag und soll trotzdem seinen Namen zeigen).
 */
export function nonWorkingDay(year: number, month: number, day: number): NonWorkingDay | null {
  const name = nrwHolidays(year).get(key(year, month, day));
  if (name) return { kind: 'holiday', name };
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return weekday === 0 || weekday === 6 ? { kind: 'weekend' } : null;
}
